import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import Select from 'react-select';
import { toast } from 'react-hot-toast';
import { prescriptionAPI, icdAPI } from '../services/api';
import { Download, Save, FileText, Trash2, Shield } from 'lucide-react';
// import decorativeBg from './assets/prescription-image.png'; // The curved background element

const PrescriptionDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [prescription, setPrescription] = useState(null);
  const [icdOptions, setIcdOptions] = useState([]);
  const [selectedIcdCodes, setSelectedIcdCodes] = useState([]);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();

  const apiBaseUrl = useMemo(() => {
    const envUrl = process.env.REACT_APP_API_URL;
    return envUrl && envUrl.length > 0 ? envUrl : 'http://localhost:8000';
  }, []);

  const fileUrl = useMemo(() => {
    if (!prescription?.file_path) return '';
    return `${apiBaseUrl}/uploads/${prescription.file_path}`;
  }, [apiBaseUrl, prescription]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [codesRes, prescriptionRes] = await Promise.all([
          icdAPI.getAll(),
          prescriptionAPI.getById(id),
        ]);

        const options = codesRes.data.map((code) => ({
          value: code.code,
          label: `${code.code} - ${code.description}`,
        }));
        setIcdOptions(options);

        const presc = prescriptionRes.data;
        setPrescription(presc);

        reset({ patient_name: presc.patient_name });

        const selected = (presc.icd_codes || [])
          .map((code) => options.find((opt) => opt.value === code))
          .filter(Boolean);
        setSelectedIcdCodes(selected);
      } catch (error) {
        toast.error('Failed to load prescription');
        navigate('/dashboard'); // Redirect if prescription not found
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, reset, navigate]);

  const onSubmit = async (data) => {
    setSaving(true);
    try {
      const payload = {
        patient_name: data.patient_name,
        icd_codes: selectedIcdCodes.map((c) => c.value),
      };
      const res = await prescriptionAPI.update(id, payload);
      setPrescription(res.data);
      toast.success('Prescription updated successfully!');
      navigate('/dashboard'); // Navigate back after successful save
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = async () => {
    try {
      const response = await prescriptionAPI.download(id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', prescription?.file_name || 'prescription');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download');
    }
  };
  
    // You may want a delete function as well, matching the red button in the UI
  const handleDelete = async () => {
    if (window.confirm("Are you sure you want to delete this prescription? This action cannot be undone.")) {
        try {
            await prescriptionAPI.delete(id);
            toast.success("Prescription deleted successfully");
            navigate('/dashboard');
        } catch (error) {
            toast.error("Failed to delete prescription");
        }
    }
  };


  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="spinner"></div>
      </div>
    );
  }

  const isPdf = prescription.file_type === 'application/pdf';

  return (
        <div className="w-full">  
        
        <div className="text-center mb-8 z-10">
            <h1 className="text-2xl  md:text-3xl  font-bold text-gray-800 flex items-center justify-start gap-3">
                Prescription Detail 
            </h1>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 w-full z-10">
            {/* Left Panel: Form */}
            <div className="bg-white rounded-2xl shadow-xl p-8 space-y-6">
                <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col h-full">
                    <div className="flex-1 space-y-6">
                        <div>
                            <label htmlFor="patient_name" className="text-sm font-medium text-gray-700 mb-1 block">Patient name</label>
                            <input
                                id="patient_name"
                                type="text"
                                className="pl-2 form-input"
                                {...register('patient_name', { required: 'Patient name is required' })}
                            />
                            {errors.patient_name && <p className="mt-1 text-sm text-red-600">{errors.patient_name.message}</p>}
                        </div>
                        <div>
                            <label className="text-sm font-medium text-gray-700 mb-1 block">ICD Codes <span className="text-gray-400">(At least one)</span></label>
                            <Select
                                isMulti
                                options={icdOptions}
                                value={selectedIcdCodes}
                                onChange={(opts) => setSelectedIcdCodes(opts || [])}
                                placeholder="Select ICD codes..."
                                className="react-select-container"
                                classNamePrefix="react-select"
                            />
                        </div>
                    </div>
                    <div className="mt-8 flex gap-4">
                        <button type="button" onClick={() => navigate('/dashboard')} className="flex-1 py-3 px-4 bg-gray-200 text-gray-800 font-semibold rounded-lg hover:bg-gray-300">Cancel</button>
                        <button type="submit" disabled={saving} className="flex-1 py-3 px-4 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2">
                            {saving ? <div className="spinner-white"></div> : <Save className="w-5 h-5" />}
                            {saving ? 'Saving...' : 'Save'}
                        </button>
                    </div>
                </form>
            </div>

            {/* Right Panel: File Preview */}
            <div className="bg-white rounded-2xl shadow-xl p-6 flex flex-col relative"> {/* <-- Step 1: Added 'relative' */}
                
                {/* Step 2: The button group is now positioned absolutely */}
                <div className="absolute top-6 right-6 flex items-center gap-2">
                    <button onClick={handleDownload} className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"><Download className="w-5 h-5" /></button>
                    <button onClick={handleDelete} className="p-2 bg-red-600 text-white rounded-lg hover:bg-red-700"><Trash2 className="w-5 h-5" /></button>
                </div>

                {/* The header no longer needs 'justify-between' */}
                <div className="flex items-center pb-4">
                    <div className="flex items-center gap-3 pr-[7rem]"> {/* <-- Step 3: Added right padding to prevent text overlap */}
                        <FileText className="w-6 h-6 text-blue-600 flex-shrink-0" /> {/* Added flex-shrink-0 to protect icon */}
                        <div>
                            {/* The 'truncate' class is added for extra safety to prevent long text from wrapping awkwardly */}
                            <p className="font-semibold text-gray-800 break-words">{prescription.file_name}</p>
                            <p className="text-xs text-gray-500">Uploaded on {new Date(prescription.created_at).toLocaleDateString()}</p>
                        </div>
                    </div>
                </div>

                <div className="items-center flex-1 bg-gray-50 rounded-lg p-4 border border-gray-200 overflow-hidden">
                    {isPdf ? (
                        <iframe title="Prescription PDF" src={fileUrl} className="w-full h-full" />
                    ) : (
                        <img src={fileUrl} alt="Prescription" className="w-full h-64 object-contain" />
                    )}
                </div>
            </div>
        </div>
    </div>
  );
};

export default PrescriptionDetail;